/**
 * Reusable in-page category manager for the Workspace sections
 * (notes / writings / documents / quotes / todos).
 *
 * Supports hierarchical categories (unlimited depth): every category can hold
 * sub-categories via a `parent_id` link; the backend promotes direct children
 * up one level when a parent is deleted so sub-trees survive.
 *
 * Usage:
 *   const { categories, invalidate } = useCategories("writings");
 *   // Put <CategorySelect/> inside your form and <CategoryManager/>
 *   // somewhere at page-level so the admin can add/edit/delete.
 *
 * Modal behaviour: clicking the dim backdrop never closes the modal — only
 * the X button, Kapat button or ESC key does. This protects the admin from
 * losing unsaved category edits by an accidental click.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Settings2, Plus, Pencil, Trash2, X, Save, FolderTree, ChevronRight, ChevronDown, CornerDownRight,
} from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const COLOR_PRESETS = [
  { key: "indigo",  cls: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  { key: "emerald", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { key: "rose",    cls: "bg-rose-100 text-rose-800 border-rose-200" },
  { key: "amber",   cls: "bg-amber-100 text-amber-800 border-amber-200" },
  { key: "sky",     cls: "bg-sky-100 text-sky-800 border-sky-200" },
  { key: "violet",  cls: "bg-violet-100 text-violet-800 border-violet-200" },
  { key: "teal",    cls: "bg-teal-100 text-teal-800 border-teal-200" },
  { key: "slate",   cls: "bg-slate-100 text-slate-800 border-slate-200" },
  { key: "pink",    cls: "bg-pink-100 text-pink-800 border-pink-200" },
  { key: "orange",  cls: "bg-orange-100 text-orange-800 border-orange-200" },
];

export function colorClasses(color) {
  const match = COLOR_PRESETS.find((c) => c.key === color);
  return match ? match.cls : COLOR_PRESETS[0].cls;
}

export function CategoryBadge({ name, color, onClick, selected = false, className = "" }) {
  if (!name) return null;
  const base = colorClasses(color || "indigo");
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-[11px] rounded-full border px-2 py-0.5 transition ${base} ${
        selected ? "ring-2 ring-offset-1 ring-current" : "hover:scale-105"
      } ${className}`}
      data-testid={`cat-badge-${name}`}
    >
      {name}
    </button>
  );
}

export function useCategories(kind) {
  const qc = useQueryClient();
  const { data = [] } = useQuery({
    queryKey: ["workspace", "categories", kind],
    queryFn: () => api.get("/admin/workspace/categories", { params: { kind } }).then((r) => r.data),
    staleTime: 60_000,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["workspace", "categories", kind] });
  return { categories: data, invalidate };
}

// -- Tree helpers ----------------------------------------------------------
/** Build an id→children[] map and a sorted roots list from a flat list. */
function buildTree(flat) {
  const byParent = new Map();
  for (const c of flat) {
    const pid = c.parent_id || null;
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid).push(c);
  }
  for (const arr of byParent.values()) arr.sort((a, b) => a.name.localeCompare(b.name, "tr"));
  return { byParent, roots: byParent.get(null) || [] };
}

/** Collect every descendant id of the given node (self excluded) so the parent
 *  picker can hide them when editing — a category can't be moved under its own
 *  descendant. */
function collectDescendants(byParent, nodeId, out = new Set()) {
  for (const c of byParent.get(nodeId) || []) {
    out.add(c.id);
    collectDescendants(byParent, c.id, out);
  }
  return out;
}

/** Produce a flat "Parent / Sub / Sub-sub" label for every node, keyed by id.
 *  Used by CategorySelect so the admin can pick a leaf category and still see
 *  the full hierarchy context in the dropdown. */
function buildPathIndex(flat) {
  const byId = new Map(flat.map((c) => [c.id, c]));
  const cache = new Map();
  const compute = (id) => {
    if (cache.has(id)) return cache.get(id);
    const node = byId.get(id);
    if (!node) return "";
    const parent = node.parent_id ? compute(node.parent_id) : "";
    const path = parent ? `${parent} / ${node.name}` : node.name;
    cache.set(id, path);
    return path;
  };
  for (const c of flat) compute(c.id);
  return cache;
}

// -- Row --------------------------------------------------------------------
// Pure renderer for a single flattened tree entry; no self-recursion so Babel
// doesn't choke and nothing gets confused about hook ordering.
function CategoryRow({
  node, depth, hasChildren, childCount, isOpen,
  toggleExpand, onEdit, onDelete, onAddChild, editingId,
}) {
  const isEditing = editingId === node.id;
  return (
    <li className={`flex items-center gap-2 border rounded-lg px-2 py-1.5 bg-white hover:bg-stone-50 transition ${
          isEditing ? "ring-2 ring-indigo-400" : ""
        }`}
        style={{ marginLeft: depth * 20 }}
        data-testid={`cat-row-${node.id}`}>
      <button
        type="button"
        onClick={() => hasChildren && toggleExpand(node.id)}
        className={`p-0.5 rounded ${hasChildren ? "hover:bg-stone-100 text-stone-600" : "text-transparent"}`}
        disabled={!hasChildren}
        data-testid={`cat-expand-${node.id}`}
      >
        {hasChildren
          ? (isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />)
          : <ChevronRight size={14} />}
      </button>
      {depth > 0 && <CornerDownRight size={12} className="text-stone-300 shrink-0" />}
      <CategoryBadge name={node.name} color={node.color} />
      {hasChildren && (
        <span className="text-[10px] text-stone-400">({childCount})</span>
      )}
      <span className="flex-1" />
      <button
        type="button"
        onClick={() => onAddChild(node)}
        className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700"
        title="Alt kategori ekle"
        data-testid={`cat-addchild-${node.id}`}
      >
        <Plus size={13} />
      </button>
      <button
        type="button"
        onClick={() => onEdit(node)}
        className="p-1.5 rounded hover:bg-indigo-50 text-indigo-700"
        title="Düzenle"
        data-testid={`cat-edit-${node.id}`}
      >
        <Pencil size={13} />
      </button>
      <button
        type="button"
        onClick={() => onDelete(node)}
        className="p-1.5 rounded hover:bg-red-50 text-red-600"
        title="Sil"
        data-testid={`cat-delete-${node.id}`}
      >
        <Trash2 size={13} />
      </button>
    </li>
  );
}

/** DFS the tree and emit a flat list of visible rows (respects expand state). */
function flattenTree(roots, byParent, expanded) {
  const out = [];
  const walk = (nodes, depth) => {
    for (const n of nodes) {
      const kids = byParent.get(n.id) || [];
      out.push({ node: n, depth, hasChildren: kids.length > 0, childCount: kids.length });
      if (kids.length && expanded.has(n.id)) walk(kids, depth + 1);
    }
  };
  walk(roots, 0);
  return out;
}

// -- Modal ------------------------------------------------------------------
export default function CategoryManager({ open, onClose, kind, title = "Kategorileri yönet" }) {
  const { categories, invalidate } = useCategories(kind);
  const [name, setName] = useState("");
  const [color, setColor] = useState("indigo");
  const [parentId, setParentId] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  const { byParent, roots } = useMemo(() => buildTree(categories), [categories]);
  const pathIndex = useMemo(() => buildPathIndex(categories), [categories]);

  // ESC closes the modal, but a background click never does — this protects
  // half-typed sub-categories from being lost on an accidental backdrop click.
  // Use capture + stopImmediatePropagation so when the manager is stacked on
  // top of another modal (e.g. the writing editor) only this one closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  // Default-expand every root the first time we open the modal.
  useEffect(() => {
    if (open && expanded.size === 0 && roots.length > 0) {
      setExpanded(new Set(roots.map((r) => r.id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Hide the category itself + all descendants from the parent picker when
  // editing — moving under a descendant would create a cycle.
  const disabledIds = useMemo(() => {
    if (!editingId) return new Set();
    const d = collectDescendants(byParent, editingId);
    d.add(editingId);
    return d;
  }, [editingId, byParent]);

  if (!open) return null;

  const toggleExpand = (id) => {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const reset = () => { setName(""); setColor("indigo"); setParentId(""); setEditingId(null); };

  const beginEdit = (c) => {
    setEditingId(c.id);
    setName(c.name);
    setColor(c.color || "indigo");
    setParentId(c.parent_id || "");
  };

  const beginChild = (parent) => {
    reset();
    setParentId(parent.id);
    setExpanded((cur) => new Set(cur).add(parent.id));
    toast.success(`Alt kategori: ${parent.name}`, { description: "Adını yazıp Ekle'ye bas." });
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Kategori adı girin"); return; }
    setBusy(true);
    try {
      if (editingId) {
        // null (not ""/undefined) sends "no parent" explicitly.
        await api.put(`/admin/workspace/categories/${editingId}`, {
          name: trimmed, color, parent_id: parentId || null,
        });
        toast.success("Kategori güncellendi");
      } else {
        await api.post("/admin/workspace/categories", {
          kind, name: trimmed, color, parent_id: parentId || null,
        });
        toast.success("Kategori oluşturuldu");
        // Auto-expand the new parent so the admin instantly sees where the
        // category landed in the tree.
        if (parentId) setExpanded((cur) => new Set(cur).add(parentId));
      }
      reset();
      invalidate();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const remove = async (c) => {
    const kids = byParent.get(c.id) || [];
    const msg = kids.length
      ? `"${c.name}" ve ${kids.length} alt kategorisinin alt bağlantıları bir üst seviyeye alınacak, kategori silinecek. Devam?`
      : `"${c.name}" kategorisi silinecek. Bu kategorideki kayıtlar kategorisiz kalır. Onaylıyor musunuz?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/workspace/categories/${c.id}`);
      toast.success("Silindi");
      invalidate();
      if (editingId === c.id) reset();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[90] flex items-center justify-center p-4 animate-in fade-in"
         data-testid="cat-manager-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden animate-in zoom-in-95"
           data-testid={`cat-manager-${kind}`}>
        <div className="px-5 py-4 border-b bg-gradient-to-r from-indigo-50 to-violet-50 flex items-center gap-2">
          <FolderTree size={18} className="text-indigo-600" />
          <div className="flex-1">
            <h2 className="font-semibold text-slate-900 leading-tight">{title}</h2>
            <p className="text-[11px] text-stone-500">Alt kategoriler sınırsız derinlikte olabilir.</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/60" data-testid="cat-manager-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-5">
          {/* Add / edit form */}
          <div className="bg-indigo-50/40 border border-indigo-100 rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-indigo-900">
                {editingId ? "Kategoriyi düzenle" : parentId ? "Alt kategori ekle" : "Yeni kategori"}
              </label>
              {(editingId || parentId) && (
                <button type="button" onClick={reset} className="text-[11px] text-indigo-700 hover:underline"
                        data-testid="cat-cancel-edit">
                  Üst seviyeye dön
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Örn: Siyaset, Edebiyat..."
                className="h-9 text-sm"
                maxLength={60}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } }}
                data-testid="cat-input-name"
                autoFocus
              />
            </div>
            <div className="mb-2">
              <label className="text-[11px] font-semibold text-stone-600 block mb-1">Üst kategori</label>
              <select
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                className="w-full h-9 border rounded-md px-2 text-sm bg-white"
                data-testid="cat-input-parent"
              >
                <option value="">— Üst seviye —</option>
                {categories
                  .filter((c) => !disabledIds.has(c.id))
                  .sort((a, b) => (pathIndex.get(a.id) || "").localeCompare(pathIndex.get(b.id) || "", "tr"))
                  .map((c) => (
                    <option key={c.id} value={c.id}>{pathIndex.get(c.id) || c.name}</option>
                  ))}
              </select>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mb-3">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setColor(c.key)}
                  className={`w-7 h-7 rounded-full border ${c.cls} ${
                    color === c.key ? "ring-2 ring-indigo-500 scale-110" : "hover:scale-110"
                  }`}
                  title={c.key}
                  data-testid={`cat-color-${c.key}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={save} disabled={busy} data-testid="cat-save">
                <Save size={13} className="mr-1" /> {editingId ? "Güncelle" : "Ekle"}
              </Button>
            </div>
          </div>

          {/* Tree */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider">
                Mevcut kategoriler ({categories.length})
              </div>
              {categories.length > 0 && (
                <button
                  type="button"
                  onClick={() => setExpanded(expanded.size === 0 ? new Set(categories.map((c) => c.id)) : new Set())}
                  className="text-[11px] text-indigo-700 hover:underline"
                  data-testid="cat-toggle-all"
                >
                  {expanded.size === 0 ? "Tümünü aç" : "Tümünü kapat"}
                </button>
              )}
            </div>
            {categories.length === 0 ? (
              <p className="text-sm text-stone-400 italic text-center py-6">
                Henüz kategori yok.
              </p>
            ) : (
              <ul className="space-y-1" data-testid="cat-list">
                {flattenTree(roots, byParent, expanded).map((row) => (
                  <CategoryRow
                    key={row.node.id}
                    node={row.node}
                    depth={row.depth}
                    hasChildren={row.hasChildren}
                    childCount={row.childCount}
                    isOpen={expanded.has(row.node.id)}
                    toggleExpand={toggleExpand}
                    onEdit={beginEdit}
                    onDelete={remove}
                    onAddChild={beginChild}
                    editingId={editingId}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t bg-stone-50 flex justify-end">
          <Button size="sm" variant="outline" onClick={onClose} data-testid="cat-manager-done">
            Kapat
          </Button>
        </div>
      </div>
    </div>
  );
}

export function CategorySelect({ value, onChange, kind, placeholder = "— seçiniz —", testid, onManage }) {
  const { categories } = useCategories(kind);
  const pathIndex = useMemo(() => buildPathIndex(categories), [categories]);
  // Sorted by full dotted path so sub-categories sit under their parents in the dropdown.
  const sorted = useMemo(
    () => [...categories].sort((a, b) =>
      (pathIndex.get(a.id) || a.name).localeCompare(pathIndex.get(b.id) || b.name, "tr"),
    ),
    [categories, pathIndex],
  );
  return (
    <div className="flex items-center gap-1.5">
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 h-9 border rounded-md px-3 text-sm bg-white"
        data-testid={testid || "cat-select"}
      >
        <option value="">{placeholder}</option>
        {sorted.map((c) => (
          <option key={c.id} value={c.name}>{pathIndex.get(c.id) || c.name}</option>
        ))}
      </select>
      {onManage && (
        <button type="button" onClick={onManage}
                className="p-2 rounded-md border text-stone-500 hover:bg-indigo-50 hover:text-indigo-600"
                title="Kategorileri yönet"
                data-testid={`${testid || "cat-select"}-manage`}>
          <Plus size={14} />
        </button>
      )}
    </div>
  );
}

// -- Sidebar ----------------------------------------------------------------
/** Return the set of category NAMES that are `name` or any of its descendants.
 *  Items on this page store `category` as a plain name string, so filtering
 *  by name is enough — the sidebar can light up a parent and still include
 *  rows that live in a sub-category. */
function collectDescendantNames(categories, byParent, rootId) {
  const names = new Set();
  const walk = (id) => {
    const node = categories.find((c) => c.id === id);
    if (node) names.add(node.name);
    for (const kid of byParent.get(id) || []) walk(kid.id);
  };
  walk(rootId);
  return names;
}

/** True if `item.category` falls under the sidebar-selected `activeId` subtree
 *  (or activeId is empty → always true). Hoisted so page files can call it
 *  via `matchCategoryFilter(items, activeId, categories)` without duplicating. */
export function matchCategoryFilter(category, activeId, categories) {
  if (!activeId) return true;
  const { byParent } = buildTree(categories);
  const names = collectDescendantNames(categories, byParent, activeId);
  return names.has(category || "");
}

/**
 * Vertical sidebar listing a kind's categories as a collapsible menu.
 *
 * Props:
 *   - kind: 'writings' | 'notes' | 'documents' | 'quotes' | 'todos'
 *   - items: current filtered-by-everything-but-category array (used for counts)
 *   - itemCategory: `(item) => string` resolver for the category name on an item
 *   - value: active category id (empty = all)
 *   - onChange: `(categoryId) => void`
 *   - onManage: opens the CategoryManager modal
 *   - accent: tailwind color token (rose / indigo / amber / sky / emerald) for themeing
 *   - allLabel: label of the top "all" row (e.g. "Tüm Yazılar")
 */
export function CategorySidebar({
  kind,
  items = [],
  itemCategory = (it) => it.category || "",
  value = "",
  onChange,
  onManage,
  accent = "indigo",
  allLabel = "Tüm Kategoriler",
}) {
  const { categories } = useCategories(kind);
  const { byParent, roots } = useMemo(() => buildTree(categories), [categories]);
  const [expanded, setExpanded] = useState(() => new Set());

  // Default-expand the ancestor chain of the currently-selected category so
  // navigating straight to a leaf shows it in context on first render.
  useEffect(() => {
    if (!value || expanded.size > 0) return;
    const path = new Set();
    let cur = categories.find((c) => c.id === value);
    while (cur && cur.parent_id) {
      path.add(cur.parent_id);
      cur = categories.find((c) => c.id === cur.parent_id);
    }
    if (path.size) setExpanded(path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, categories.length]);

  // Per-category item count including every descendant's items.
  const subtreeCount = useMemo(() => {
    const perName = new Map();
    for (const it of items) {
      const n = itemCategory(it);
      perName.set(n, (perName.get(n) || 0) + 1);
    }
    const counts = new Map();
    const compute = (id) => {
      const node = categories.find((c) => c.id === id);
      if (!node) return 0;
      let total = perName.get(node.name) || 0;
      for (const kid of byParent.get(id) || []) total += compute(kid.id);
      counts.set(id, total);
      return total;
    };
    for (const r of roots) compute(r.id);
    return counts;
  }, [items, itemCategory, categories, byParent, roots]);

  const toggle = (id) => {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // DFS to a flat list of visible rows for rendering.
  const visible = useMemo(() => {
    const out = [];
    const walk = (nodes, depth) => {
      for (const n of nodes) {
        const kids = byParent.get(n.id) || [];
        out.push({ node: n, depth, hasChildren: kids.length > 0 });
        if (kids.length && expanded.has(n.id)) walk(kids, depth + 1);
      }
    };
    walk(roots, 0);
    return out;
  }, [roots, byParent, expanded]);

  const accentActive = {
    rose:    "bg-rose-500 text-white border-rose-500",
    indigo:  "bg-indigo-500 text-white border-indigo-500",
    amber:   "bg-amber-500 text-white border-amber-500",
    sky:     "bg-sky-500 text-white border-sky-500",
    emerald: "bg-emerald-500 text-white border-emerald-500",
    violet:  "bg-violet-500 text-white border-violet-500",
  }[accent] || "bg-indigo-500 text-white border-indigo-500";

  const accentSoft = {
    rose:    "border-rose-100 bg-gradient-to-b from-rose-50/50 to-white",
    indigo:  "border-indigo-100 bg-gradient-to-b from-indigo-50/50 to-white",
    amber:   "border-amber-100 bg-gradient-to-b from-amber-50/50 to-white",
    sky:     "border-sky-100 bg-gradient-to-b from-sky-50/50 to-white",
    emerald: "border-emerald-100 bg-gradient-to-b from-emerald-50/50 to-white",
    violet:  "border-violet-100 bg-gradient-to-b from-violet-50/50 to-white",
  }[accent] || "border-indigo-100 bg-gradient-to-b from-indigo-50/50 to-white";

  const accentHeader = {
    rose:    "from-rose-100 to-amber-50 text-rose-800",
    indigo:  "from-indigo-100 to-violet-50 text-indigo-800",
    amber:   "from-amber-100 to-orange-50 text-amber-800",
    sky:     "from-sky-100 to-indigo-50 text-sky-800",
    emerald: "from-emerald-100 to-teal-50 text-emerald-800",
    violet:  "from-violet-100 to-indigo-50 text-violet-800",
  }[accent] || "from-indigo-100 to-violet-50 text-indigo-800";

  const dotCls = (color) => {
    const map = {
      indigo: "bg-indigo-500", rose: "bg-rose-500", emerald: "bg-emerald-500",
      amber: "bg-amber-500", sky: "bg-sky-500", violet: "bg-violet-500",
      teal: "bg-teal-500", slate: "bg-slate-500", pink: "bg-pink-500",
      orange: "bg-orange-500",
    };
    return map[color] || "bg-indigo-500";
  };

  return (
    <aside
      className={`rounded-2xl border shadow-sm overflow-hidden flex flex-col lg:sticky lg:top-4 self-start ${accentSoft}`}
      data-testid={`cat-sidebar-${kind}`}
    >
      <div className={`px-3 py-2.5 bg-gradient-to-r border-b flex items-center gap-2 ${accentHeader}`}>
        <FolderTree size={14} />
        <span className="text-xs font-bold uppercase tracking-wider flex-1">Kategoriler</span>
        {onManage && (
          <button
            type="button"
            onClick={onManage}
            className="p-1 rounded hover:bg-white/50"
            title="Kategorileri yönet"
            data-testid={`cat-sidebar-${kind}-manage`}
          >
            <Settings2 size={13} />
          </button>
        )}
      </div>

      <ul className="flex-1 overflow-y-auto p-2 space-y-0.5 min-h-[200px] max-h-[65vh]">
        <li>
          <button
            type="button"
            onClick={() => onChange?.("")}
            className={`w-full text-left px-2.5 py-2 rounded-lg flex items-center gap-2 text-sm border transition ${
              !value ? accentActive + " font-semibold" : "border-transparent hover:bg-white hover:border-stone-200 text-slate-700"
            }`}
            data-testid={`cat-sidebar-all-${kind}`}
          >
            <FolderTree size={13} className="shrink-0" />
            <span className="flex-1 truncate">{allLabel}</span>
            <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${!value ? "bg-white/25" : "bg-stone-100 text-stone-500"}`}>
              {items.length}
            </span>
          </button>
        </li>

        {categories.length === 0 && (
          <li className="px-2 py-6 text-center text-[11px] text-stone-400 italic">
            Henüz kategori yok.
            {onManage && (
              <button onClick={onManage} className="block mx-auto mt-2 text-indigo-600 hover:underline">
                İlk kategorini oluştur
              </button>
            )}
          </li>
        )}

        {visible.map(({ node, depth, hasChildren }) => {
          const active = value === node.id;
          const count = subtreeCount.get(node.id) || 0;
          const isOpen = expanded.has(node.id);
          return (
            <li key={node.id}>
              <div
                className={`group flex items-center gap-1 rounded-lg border transition ${
                  active ? accentActive + " font-semibold" : "border-transparent hover:bg-white hover:border-stone-200 text-slate-700"
                }`}
                style={{ marginLeft: depth * 10 }}
              >
                <button
                  type="button"
                  onClick={() => hasChildren && toggle(node.id)}
                  className={`shrink-0 p-1 rounded ${
                    hasChildren
                      ? (active ? "hover:bg-white/20" : "hover:bg-stone-100 text-stone-500")
                      : "opacity-0 pointer-events-none"
                  }`}
                  tabIndex={hasChildren ? 0 : -1}
                  data-testid={`cat-sidebar-expand-${node.id}`}
                  aria-label={isOpen ? "Alt kategorileri kapat" : "Alt kategorileri aç"}
                >
                  {hasChildren
                    ? (isOpen ? <ChevronDown size={13} /> : <Plus size={13} />)
                    : <ChevronRight size={13} />}
                </button>
                <button
                  type="button"
                  onClick={() => onChange?.(active ? "" : node.id)}
                  className="flex-1 py-1.5 pr-2 flex items-center gap-2 text-sm text-left min-w-0"
                  data-testid={`cat-sidebar-pick-${node.id}`}
                >
                  <span className={`w-2 h-2 rounded-full shrink-0 ${dotCls(node.color)}`} />
                  <span className="flex-1 truncate">{node.name}</span>
                  {count > 0 && (
                    <span className={`text-[10px] rounded-full px-1.5 py-0.5 shrink-0 ${
                      active ? "bg-white/25" : "bg-stone-100 text-stone-500"
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {onManage && categories.length > 0 && (
        <button
          type="button"
          onClick={onManage}
          className={`w-full py-2 border-t text-[11px] font-semibold flex items-center justify-center gap-1 bg-gradient-to-r ${accentHeader} hover:brightness-95 transition`}
          data-testid={`cat-sidebar-${kind}-add`}
        >
          <Settings2 size={11} />
          Kategorileri yönet
        </button>
      )}
    </aside>
  );
}
